import type { ChatReplyToPreview } from "@/lib/chat/reply-to-preview"

export type ChatMessage = {
  id: string
  content: string
  user: { name: string; avatar_url?: string | null }
  createdAt: string
  senderId?: string
  clientKey?: string
  imageUrl?: string | null
  pending?: boolean
  listing?: any
  order?: any
  attachmentId?: string | null
  listingId?: string | null
  replyToMessageId?: string | null
  replyTo?: ChatReplyToPreview | null
  reactions?: Record<string, number> | null
  viewerReaction?: string | null
}

export type ChatMessageApiRow = {
  id: string
  content: string
  created_at: string
  sender_id: string
  image_url?: string | null
  profiles?:
    | { username?: string | null; avatar_url?: string | null }
    | Array<{ username?: string | null; avatar_url?: string | null }>
    | null
  listing?: unknown
  listing_id?: string | null
  order?: unknown
  order_id?: string | null
  seller_order_id?: string | null
  attachment_id?: string | null
  reply_to_message_id?: string | null
  replyTo?: ChatReplyToPreview | null
  reactions?: Record<string, number> | null
  viewerReaction?: string | null
}

function resolveProfile(
  profiles: ChatMessageApiRow["profiles"],
): { username?: string | null; avatar_url?: string | null } | null {
  if (!profiles) return null
  return Array.isArray(profiles) ? (profiles[0] ?? null) : profiles
}

export function mapApiMessageRow(m: ChatMessageApiRow): ChatMessage {
  const profile = resolveProfile(m.profiles)
  const normalized = normalizeOrder(m.order)
  const fallbackOrder =
    !normalized && (m.order_id || m.seller_order_id)
      ? normalizeOrder({
          id: m.order_id ?? undefined,
          orderId: m.order_id ?? undefined,
          orderNumber: undefined,
          sellerOrderId: m.seller_order_id ?? undefined,
          status: "pending",
          total: 0,
          itemCount: null,
        })
      : undefined
  const normalizedListing = normalizeListing({ listing: m.listing, listing_id: m.listing_id })
  const fallbackListing =
    !normalizedListing && m.listing_id
      ? {
          id: m.listing_id,
          user_id: null,
          visibility: null,
          chat_private_cancelled_at: null,
          deleted_at: null,
          title: m.content && m.content !== "[Listing]" ? m.content : "Listing",
          description: "",
          price: null,
          currency: null,
          quantity: null,
          image_urls: [],
          shipping_config: null,
          condition: "",
          grade: "",
          location: null,
          seller_username: profile?.username ?? null,
          seller_avatar_url: profile?.avatar_url ?? null,
        }
      : undefined

  return {
    id: m.id,
    content: m.content,
    createdAt: m.created_at,
    senderId: m.sender_id,
    imageUrl: m.image_url,
    user: {
      name: profile?.username || m.sender_id,
      avatar_url: profile?.avatar_url || null,
    },
    clientKey: m.id,
    listing: normalizedListing ?? fallbackListing,
    order: normalized ?? fallbackOrder,
    attachmentId: m.attachment_id ?? null,
    listingId:
      m.listing_id ??
      (normalizedListing?.id != null ? String(normalizedListing.id) : null) ??
      (fallbackListing?.id != null ? String(fallbackListing.id) : null) ??
      null,
    replyToMessageId: m.reply_to_message_id ?? null,
    replyTo: m.replyTo ?? null,
    reactions: m.reactions ?? null,
    viewerReaction: m.viewerReaction ?? null,
  }
}

export function normalizeOrder(raw: unknown) {
  if (!raw) return undefined
  const o = typeof raw === "string" ? safeParseJSON(raw) : (raw as Record<string, unknown>)
  if (!o) return undefined
  return {
    orderId: (o.orderId ?? o.id ?? o.order_id ?? null) as string | null,
    orderNumber: (o.orderNumber ?? o.order_number ?? o.id ?? o.order_id ?? null) as string | null,
    total: typeof o.total === "number" ? o.total : Number(o.total ?? 0),
    currency: (o.currency ?? null) as string | null,
    status: (o.status ?? "pending") as string,
    date: (o.date ?? o.created_at ?? o.createdAt ?? null) as string | null,
    itemCount: typeof o.itemCount === "number" ? o.itemCount : ((o.item_count as number | null) ?? null),
    firstItemImage: (o.firstItemImage ?? o.first_item_image ?? null) as string | null,
    firstItemTitle: (o.firstItemTitle ?? o.first_item_title ?? null) as string | null,
    userId: (o.userId ?? o.user_id ?? null) as string | null,
    buyerName: (o.buyerName ?? o.buyer_name ?? null) as string | null,
    sellerName: (o.sellerName ?? o.seller_name ?? null) as string | null,
    buyerAvatar: (o.buyerAvatar ?? o.buyer_avatar ?? null) as string | null,
    sellerAvatar: (o.sellerAvatar ?? o.seller_avatar ?? null) as string | null,
    sellerOrderId: (o.sellerOrderId ?? o.seller_order_id ?? null) as string | null,
  }
}

export function normalizeListing(input: { listing?: unknown; listing_id?: string | null }) {
  const rawValue = Array.isArray(input.listing) ? input.listing[0] : input.listing
  const raw =
    typeof rawValue === "string"
      ? (() => {
          try {
            return JSON.parse(rawValue)
          } catch {
            return undefined
          }
        })()
      : rawValue
  if (!raw || typeof raw !== "object") {
    if (input.listing_id) {
      return {
        id: input.listing_id,
        visibility: null,
        user_id: null,
        title: "Listing",
        description: "",
        price: null,
        currency: null,
        quantity: null,
        chat_private_cancelled_at: null,
        deleted_at: null,
        image_urls: [],
        shipping_config: null,
        condition: "",
        grade: "",
        location: null,
        seller_username: null,
        seller_avatar_url: null,
      }
    }
    return undefined
  }
  const record = raw as Record<string, unknown>
  const seller = record.profiles || record.seller || record.profile
  const sellerRecord = Array.isArray(seller) ? seller[0] : seller
  return {
    id: record.id,
    visibility: record.visibility ?? null,
    user_id: record.user_id ?? record.userId ?? null,
    title: record.title,
    description: record.description ?? "",
    price: record.price,
    currency: record.currency ?? record.currencyCode ?? record.currency_code ?? null,
    quantity: record.quantity ?? null,
    chat_private_cancelled_at: record.chat_private_cancelled_at ?? record.chatPrivateCancelledAt ?? null,
    deleted_at: record.deleted_at ?? record.deletedAt ?? null,
    image_urls: record.image_urls,
    shipping_config: record.shipping_config ?? record.shippingConfig ?? null,
    condition: record.condition,
    grade: record.grade,
    location: record.location,
    seller_username:
      (record.seller_username as string | null | undefined) ??
      (sellerRecord as { username?: string | null } | undefined)?.username,
    seller_avatar_url:
      (record.seller_avatar_url as string | null | undefined) ??
      (sellerRecord as { avatar_url?: string | null } | undefined)?.avatar_url ??
      null,
  }
}

function safeParseJSON(input: string | null | undefined) {
  if (!input) return undefined
  try {
    return JSON.parse(input)
  } catch {
    return undefined
  }
}
